import {gql} from '@apollo/client';

/** Minimal fields for tab badge counts — only the paginated total is needed. */
export const GET_ADMIN_TICKETS_MIN = gql`
  query GetAdminTicketsMin($input: AdminTicketFilterInput!) {
    getAdminTickets(input: $input) {
      ... on Error {
        message
        statusCode
        type
      }
      ... on ResponseWithHelpdeskTickets {
        message
        data {
          id
          status
        }
        pagination {
          total
        }
      }
    }
  }
`;

/** Full list row payload per admin integration guide */
export const GET_ADMIN_TICKETS = gql`
  query GetAdminTickets(
    $input: AdminTicketFilterInput!
    $eventsInput2: TicketEventFilterInput
  ) {
    getAdminTickets(input: $input) {
      ... on Error {
        message
        statusCode
        type
      }
      ... on ResponseWithHelpdeskTickets {
        message
        pagination {
          total
          previous
          pages
          page
          next
          limit
          hasPrevPage
          hasNextPage
        }
        data {
          id
          userId
          status
          origin
          contactEmail
          linkedAt
          linkedByAgentId
          agentId
          language
          product
          featureIssueId
          assignedAgentId
          assignedAgentName
          assignedAt
          awaitingAgentSince
          user {
            firstName
            lastName
            email
            id
            countryISO
            language
            imageUrl
          }
          assignedAgent {
            id
            firstName
            lastName
            email
            imageUrl
          }
          csatScore
          resolutionType
          resolvedAt
          resolvingFaqPostId
          skillsUsed {
            skillId
            source
            turnCount
            skill {
              id
              node
              tree
              domain
              status
            }
          }
          intent
          aiTurnCount
          lastAiResponseAt
          createdAt
          updatedAt
          events(input: $eventsInput2) {
            id
            eventType
            payload
            createdAt
            agent {
              id
              firstName
              lastName
              imageUrl
            }
          }
          escalation {
            reason
            priority
            team
            note
            triggeredBy
            escalatedAt
          }
          featureIssue {
            title
            stepTemplate
          }
        }
      }
    }
  }
`;

export const GET_TICKET = gql`
  query GetTicket($input: TicketFilterInput!) {
    getTicket(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithHelpdeskTicket {
        message
        data {
          id
          userId
          status
          origin
          contactEmail
          linkedAt
          linkedByAgentId
          agentId
          language
          product
          featureIssueId
          intent
          sentiment
          currentNodeId
          flowPath
          selectedOptions
          contextBundle
          aiTurnCount
          lastAiResponseAt
          assignedAgentId
          assignedAgentName
          assignedAt
          user {
            id
            firstName
            lastName
            email
            imageUrl
          }
          assignedAgent {
            id
            firstName
            lastName
            email
            imageUrl
          }
          csatScore
          csatComment
          resolutionType
          resolvedAt
          resolvingFaqPostId
          skillsUsed {
            skillId
            source
            turnCount
            skill {
              id
              node
              tree
              domain
              status
            }
          }
          openclawSessionRef
          createdAt
          updatedAt
          events {
            id
            eventType
            payload
            createdAt
            agent {
              id
              firstName
              lastName
              imageUrl
            }
          }
          escalation {
            reason
            priority
            team
            note
            triggeredBy
            escalatedAt
          }
          featureIssue {
            title
            stepTemplate
          }
        }
      }
    }
  }
`;

export const GET_USER_CONTEXT = gql`
  query GetUserContext($input: UserIdInput!) {
    getUserContext(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithUserContext {
        message
        data {
          user {
            country
            countryISO
            language
            userType
            role
            is2FaEnabled
            isDeactivated
            isDeleted
            isAccountLocked
            preferredDisplayCurrency
            hasMadeFirstDeposit {
              isCompleted
            }
            hasMadeInvestment {
              isCompleted
            }
          }
          kyc {
            kycStatus
            amlStatus
            documentCheckStatus
            provider
            kycApprovedAt
          }
          subscription {
            dabaPro {
              endDate
              nextChargeDate
            }
            simulator {
              active
            }
          }
        }
      }
    }
  }
`;

/** Web Push status for the logged-in agent; `endpoint` = this browser's sub. */
export const GET_ADMIN_PUSH_CONFIG = gql`
  query GetAdminPushConfig($input: AdminPushConfigInput) {
    getAdminPushConfig(input: $input) {
      ... on Error {
        message
        statusCode
      }
      ... on ResponseWithAdminPushStatus {
        message
        data {
          enabled
          vapidPublicKey
          subscribed
        }
      }
    }
  }
`;
